/** Extraction status badge — shows processing state with retry for failed uploads */

import { Loader2, Check, X, Minus, Clock, RotateCw, EyeOff } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useRetryExtraction } from '../../hooks/use-uploads'
import { useT, type TranslationKey } from '../../i18n'

const STATUS_CONFIG = {
  pending: { icon: Clock, labelKey: 'storage.extraction.pending', tone: 'bg-warning-soft text-warning' },
  processing: { icon: Loader2, labelKey: 'storage.extraction.processing', tone: 'bg-accent-soft text-accent-text', spin: true },
  completed: { icon: Check, labelKey: 'storage.extraction.completed', tone: 'bg-success-soft text-success' },
  failed: { icon: X, labelKey: 'storage.extraction.failed', tone: 'bg-danger-soft text-danger' },
  unsupported: { icon: Minus, labelKey: 'storage.extraction.unsupported', tone: 'bg-surface-muted text-fg-subtle' },
  excluded: { icon: EyeOff, labelKey: 'storage.extraction.excluded', tone: 'bg-surface-muted text-fg-subtle' },
} as const satisfies Record<string, { icon: unknown; labelKey: TranslationKey; tone: string; spin?: boolean }>

interface ExtractionBadgeProps {
  status: string | null
  uploadId?: string
}

export function ExtractionBadge({ status, uploadId }: ExtractionBadgeProps) {
  const t = useT()
  const config = STATUS_CONFIG[(status ?? 'pending') as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.pending
  const Icon = config.icon
  const retryExtraction = useRetryExtraction()
  const canRetry = uploadId && (status === 'failed' || status === 'pending')

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (uploadId) retryExtraction.mutate(uploadId)
  }

  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium', config.tone)}>
      <Icon className={cn('h-3 w-3', 'spin' in config && config.spin && 'animate-spin')} aria-hidden="true" />
      {t(config.labelKey)}
      {canRetry && (
        <button
          type="button"
          onClick={handleRetry}
          disabled={retryExtraction.isPending}
          className="ml-0.5 cursor-pointer rounded p-0.5 hover:bg-surface-hover disabled:cursor-not-allowed"
          title={t('storage.extraction.retry')}
          aria-label={t('storage.extraction.retry')}
        >
          <RotateCw className={cn('h-3 w-3', retryExtraction.isPending && 'animate-spin')} aria-hidden="true" />
        </button>
      )}
    </span>
  )
}
